/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_REMOTE.md
 *
 * The owner's view of remote use on an SG: data over the boat's link this
 * month (Starlink is metered), an optional monthly cap, and the last changes
 * made remotely: who, what, when. Hidden for anyone but the owner.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { formatRelativeTime } from '../../utils/formatters';

const WHAT: Record<string, string> = {
  '/api/anchor/arm': 'Armed anchor watch', '/api/anchor/disarm': 'Disarmed anchor watch',
  '/api/anchor/radius': 'Changed the anchor alarm radius', '/api/anchor/silence': 'Silenced the anchor alarm',
  '/api/recorder/start': 'Started recording', '/api/recorder/stop': 'Stopped recording',
  '/api/update/install': 'Installed the update', '/api/update/postpone': 'Postponed the update',
  '/api/sources/choices': 'Changed which instrument SG uses',
};

export const mb = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`);

export function SgRemoteDataCard({ deviceId }: { deviceId: string }) {
  const qc = useQueryClient();
  const usage = useQuery({ queryKey: ['gateway-remote-usage', deviceId], queryFn: () => api.getGatewayRemoteUsage(deviceId), retry: false });
  const log = useQuery({ queryKey: ['gateway-remote-log', deviceId], queryFn: () => api.getGatewayRemoteLog(deviceId), retry: false });
  const [cap, setCap] = useState('');
  const save = useMutation({
    mutationFn: (capMb: number | null) => api.setGatewayRemoteCap(deviceId, capMb),
    onSuccess: (u) => { qc.setQueryData(['gateway-remote-usage', deviceId], u); setCap(''); },
  });
  if (usage.isError) return null;
  const u = usage.data;
  const actions = log.data?.actions ?? [];

  return (
    <div className="bg-slate-700/50 rounded-lg p-3 space-y-2" data-testid="sg-remote-data">
      <p className="text-sm text-slate-400">Remote use</p>
      {u && (
        <p className="text-white">
          {mb(u.bytes)} over the boat’s link this month
          {u.cap_bytes !== null && <span className="text-slate-300"> · cap {mb(u.cap_bytes)}</span>}
        </p>
      )}
      {u && u.cap_bytes !== null && u.bytes >= u.cap_bytes && (
        <p className="text-sm text-amber-300">The cap is reached: remote access is paused until next month, or raise the cap.</p>
      )}
      <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); const n = Number(cap); if (n >= 1) save.mutate(Math.round(n)); }}>
        <label htmlFor={`cap-${deviceId}`} className="text-sm text-slate-300">Monthly cap</label>
        <input id={`cap-${deviceId}`} type="number" min={1} placeholder="MB" value={cap} onChange={(e) => setCap(e.target.value)}
          className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-sm" />
        <button type="submit" disabled={!(Number(cap) >= 1)} className="px-3 py-1 rounded bg-slate-600 hover:bg-slate-500 disabled:opacity-40 text-white text-sm">Set</button>
        {u?.cap_bytes !== null && u?.cap_bytes !== undefined && (
          <button type="button" onClick={() => save.mutate(null)} className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm">No cap</button>
        )}
      </form>
      {actions.length > 0 && (
        <div>
          <p className="text-sm text-slate-400 mt-2">Changes made remotely</p>
          <ul className="text-sm text-slate-200 space-y-1 mt-1">
            {actions.slice(0, 10).map((a, i) => (
              <li key={i}>
                {WHAT[a.path] ?? `${a.method} ${a.path}`}
                <span className="text-slate-400"> · {a.who ?? a.user} ({a.role}) · {formatRelativeTime(a.at)}{a.status >= 400 ? ' · refused' : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
